import React, { useState, useEffect, useCallback } from "react";
import {
  Plus, Edit2, Trash2, Send, ToggleLeft, ToggleRight, Loader2, Tag, IndianRupee, Percent
} from "lucide-react";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OffersTab = ({ auth, authAxios }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [showBlast, setShowBlast] = useState(false);
  const [blastData, setBlastData] = useState({ subject: "", message: "" });
  const [blastSending, setBlastSending] = useState(false);
  const [blastResult, setBlastResult] = useState("");
  const [formData, setFormData] = useState({
    title: "", description: "", discount_code: "",
    discount_type: "percent", discount_percent: 0, discount_amount: 0,
    valid_from: "", valid_until: "", is_active: true,
    is_coupon: false, max_uses: 0
  });

  const fetchOffers = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API_URL}/api/admin/offers`);
      setOffers(res.data);
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  const resetForm = () => {
    setFormData({
      title: "", description: "", discount_code: "",
      discount_type: "percent", discount_percent: 0, discount_amount: 0,
      valid_from: "", valid_until: "", is_active: true,
      is_coupon: false, max_uses: 0
    });
    setEditingOffer(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return;
    try {
      if (editingOffer) {
        await authAxios.put(`${API_URL}/api/admin/offers/${editingOffer.id}`, formData);
      } else {
        await authAxios.post(`${API_URL}/api/admin/offers`, formData);
      }
      await fetchOffers();
      resetForm();
    } catch (err) { logger.error(err); }
  };

  const handleEdit = (offer) => {
    setFormData({
      title: offer.title || "", description: offer.description || "",
      discount_code: offer.discount_code || "",
      discount_type: offer.discount_type || "percent",
      discount_percent: offer.discount_percent || 0,
      discount_amount: offer.discount_amount || 0,
      valid_from: offer.valid_from || "", valid_until: offer.valid_until || "",
      is_active: offer.is_active !== false,
      is_coupon: offer.is_coupon || false,
      max_uses: offer.max_uses || 0
    });
    setEditingOffer(offer);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    await authAxios.delete(`${API_URL}/api/admin/offers/${id}`);
    await fetchOffers();
  };

  const handleToggle = async (offer) => {
    await authAxios.put(`${API_URL}/api/admin/offers/${offer.id}`, { ...offer, is_active: !offer.is_active });
    await fetchOffers();
  };

  const handleSendBlast = async () => {
    if (!blastData.subject.trim() || !blastData.message.trim()) return;
    setBlastSending(true);
    setBlastResult("");
    try {
      const res = await authAxios.post(`${API_URL}/api/admin/offers/email-blast`, blastData);
      setBlastResult(res.data.message);
      setBlastData({ subject: "", message: "" });
    } catch (err) {
      setBlastResult("Failed to send emails");
    }
    setBlastSending(false);
  };

  const getDiscountLabel = (offer) => {
    if (offer.discount_type === "flat" && offer.discount_amount > 0) return `Rs ${offer.discount_amount} OFF`;
    if (offer.discount_percent > 0) return `${offer.discount_percent}% OFF`;
    return null;
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      {/* Offers List */}
      <div className="bg-white rounded-3xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">Offers & Coupons</h2>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#E83E8C] text-white rounded-xl text-sm font-medium hover:opacity-90"
            data-testid="add-offer-btn">
            <Plus size={16} /> New Offer
          </button>
        </div>

        {showForm && (
          <div className="bg-[#FFFAF5] rounded-2xl p-5 mb-6 border border-[#f0e6d9]">
            <h3 className="font-semibold text-[#1E1B4B] mb-4">{editingOffer ? "Edit Offer" : "Create Offer"}</h3>
            <div className="space-y-3">
              <input type="text" placeholder="Offer Title (e.g. Weekend Special!)" value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" data-testid="offer-title-input" />
              <textarea placeholder="Offer Description" value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})} rows={2}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" />

              {/* Coupon Toggle */}
              <label className="flex items-center gap-2.5 cursor-pointer p-2.5 rounded-xl bg-white border border-gray-100">
                <button type="button" onClick={() => setFormData({...formData, is_coupon: !formData.is_coupon})}>
                  {formData.is_coupon ? <ToggleRight size={24} className="text-[#E83E8C]" /> : <ToggleLeft size={24} className="text-gray-300" />}
                </button>
                <div>
                  <span className="text-sm font-medium text-[#1E1B4B]">Coupon Code</span>
                  <p className="text-[11px] text-[#475569]">Customers can apply this code during checkout</p>
                </div>
              </label>

              {formData.is_coupon && (
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="Code (e.g. EASTER50)" value={formData.discount_code}
                    onChange={e => setFormData({...formData, discount_code: e.target.value.toUpperCase()})}
                    className="px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm font-mono"
                    data-testid="offer-code-input" />
                  <input type="number" min="0" placeholder="Max uses (0=unlimited)" value={formData.max_uses || ""}
                    onChange={e => setFormData({...formData, max_uses: parseInt(e.target.value) || 0})}
                    className="px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" />
                </div>
              )}

              {/* Discount Type */}
              <div>
                <label className="text-xs text-[#475569] mb-2 block font-medium">Discount Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setFormData({...formData, discount_type: "percent"})}
                    className={`flex items-center justify-center gap-2 p-2.5 rounded-xl border-2 text-sm font-medium transition-all ${formData.discount_type === "percent" ? "border-[#E83E8C] bg-[#E83E8C]/5 text-[#E83E8C]" : "border-gray-200 text-[#475569]"}`}
                    data-testid="discount-type-percent">
                    <Percent size={16} /> Percentage
                  </button>
                  <button type="button" onClick={() => setFormData({...formData, discount_type: "flat"})}
                    className={`flex items-center justify-center gap-2 p-2.5 rounded-xl border-2 text-sm font-medium transition-all ${formData.discount_type === "flat" ? "border-[#E83E8C] bg-[#E83E8C]/5 text-[#E83E8C]" : "border-gray-200 text-[#475569]"}`}
                    data-testid="discount-type-flat">
                    <IndianRupee size={16} /> Flat Amount
                  </button>
                </div>
              </div>

              {/* Discount Value */}
              <div className="flex items-center gap-3">
                {formData.discount_type === "percent" ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input type="number" min="0" max="50" placeholder="%" value={formData.discount_percent || ""}
                      onChange={e => setFormData({...formData, discount_percent: parseInt(e.target.value) || 0})}
                      className="w-24 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
                      data-testid="discount-percent-input" />
                    <span className="text-sm text-[#475569] font-medium">% off</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-sm text-[#475569] font-medium">Rs</span>
                    <input type="number" min="0" max="10000" placeholder="Amount" value={formData.discount_amount || ""}
                      onChange={e => setFormData({...formData, discount_amount: parseInt(e.target.value) || 0})}
                      className="w-28 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
                      data-testid="discount-amount-input" />
                    <span className="text-sm text-[#475569] font-medium">off</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#475569] mb-1 block">Valid From</label>
                  <input type="date" value={formData.valid_from} onChange={e => setFormData({...formData, valid_from: e.target.value})}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#475569] mb-1 block">Valid Until</label>
                  <input type="date" value={formData.valid_until} onChange={e => setFormData({...formData, valid_until: e.target.value})}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" />
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <button type="button" onClick={() => setFormData({...formData, is_active: !formData.is_active})}>
                    {formData.is_active ? <ToggleRight size={28} className="text-[#25D366]" /> : <ToggleLeft size={28} className="text-gray-300" />}
                  </button>
                  <span className="text-sm text-[#1E1B4B]">{formData.is_active ? "Active" : "Inactive"}</span>
                </label>
                <div className="flex gap-2">
                  <button onClick={resetForm} className="px-4 py-2 text-[#475569] hover:bg-gray-100 rounded-xl text-sm">Cancel</button>
                  <button onClick={handleSave} className="px-4 py-2 bg-[#E83E8C] text-white rounded-xl text-sm font-medium hover:opacity-90" data-testid="save-offer-btn">Save Offer</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8"><Loader2 className="animate-spin mx-auto text-[#E83E8C]" /></div>
        ) : offers.length === 0 ? (
          <p className="text-center text-[#475569] py-8">No offers yet. Create your first offer!</p>
        ) : (
          <div className="space-y-3">
            {offers.map(offer => (
              <div key={offer.id} className={`rounded-2xl p-4 border transition-all ${offer.is_active ? "bg-[#FFFAF5] border-[#FFD166]" : "bg-gray-50 border-gray-200 opacity-60"}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-[#1E1B4B] text-sm">{offer.title}</h3>
                      {offer.is_active && <span className="px-2 py-0.5 bg-[#25D366]/10 text-[#25D366] text-xs rounded-full font-medium">Active</span>}
                      {offer.is_coupon && <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-full font-medium">Coupon</span>}
                      {getDiscountLabel(offer) && (
                        <span className="px-2 py-0.5 bg-[#E83E8C]/10 text-[#E83E8C] text-xs rounded-full font-medium">{getDiscountLabel(offer)}</span>
                      )}
                    </div>
                    <p className="text-xs text-[#475569] mb-1">{offer.description}</p>
                    <div className="flex gap-3 text-xs text-[#94a3b8] flex-wrap">
                      {offer.discount_code && <span className="font-mono">Code: <strong>{offer.discount_code}</strong></span>}
                      {offer.valid_from && <span>From: {offer.valid_from}</span>}
                      {offer.valid_until && <span>Until: {offer.valid_until}</span>}
                      {offer.usage_count > 0 && <span>Used: {offer.usage_count}x</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    <button onClick={() => handleToggle(offer)} className="p-1.5 hover:bg-white rounded-lg" title={offer.is_active ? "Deactivate" : "Activate"}>
                      {offer.is_active ? <ToggleRight size={18} className="text-[#25D366]" /> : <ToggleLeft size={18} className="text-gray-400" />}
                    </button>
                    <button onClick={() => handleEdit(offer)} className="p-1.5 hover:bg-white rounded-lg"><Edit2 size={14} className="text-[#475569]" /></button>
                    <button onClick={() => handleDelete(offer.id)} className="p-1.5 hover:bg-white rounded-lg"><Trash2 size={14} className="text-red-500" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Email Blast Section */}
      <div className="bg-white rounded-3xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-6 flex items-center gap-2">
          <Send size={20} className="text-[#E83E8C]" /> Email Blast
        </h2>
        <p className="text-sm text-[#475569] mb-4">Send promotional emails to all your past customers at once.</p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-1">Subject Line</label>
            <input type="text" value={blastData.subject} onChange={e => setBlastData({...blastData, subject: e.target.value})}
              placeholder="e.g. Weekend Special - 20% OFF on all packages!"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
              data-testid="blast-subject-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-1">Message</label>
            <textarea value={blastData.message} onChange={e => setBlastData({...blastData, message: e.target.value})}
              placeholder="Write your promotional message here..." rows={6}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
              data-testid="blast-message-input" />
          </div>
          <button onClick={handleSendBlast} disabled={blastSending || !blastData.subject.trim() || !blastData.message.trim()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white rounded-xl font-medium hover:opacity-90 disabled:opacity-50 transition-all"
            data-testid="send-blast-btn">
            {blastSending ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <><Send size={18} /> Send to All Customers</>}
          </button>
          {blastResult && (
            <div className={`px-4 py-3 rounded-xl text-sm font-medium ${blastResult.includes("Failed") ? "bg-red-50 text-red-600" : "bg-green-50 text-[#25D366]"}`}>
              {blastResult}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OffersTab;
